import React, { useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { Scroll } from './../scroll-fade';
import { StoreContext } from '../../../app/src/base/root-store';
import { FeedbackAnswerItem } from './feedback-answer-item';
import { TThumbDirection } from '../thumb';
import { TReactionType } from '../thumb/reaction';
import { IDateTime } from '../helpers';
import {CommentWithTranslation} from '../../../app/src/dashboard/team-results-store';

export interface IComment extends CommentWithTranslation{
    id: string;
    employeeId: string;
    text: string;
}

export interface ILike {
    likeCount: number;
    likedEmployeeIds: string[];
}

export interface IAnswerProps extends CommentWithTranslation{
    id?: string;
    direction: TThumbDirection;
    reactionType?: TReactionType;
    text: string;
    time?: IDateTime;
    onHasSeenCheck?: (checked: boolean) => void;
    hasSeen?: boolean;
    titleTooltip?: string | JSX.Element;
    className?: string;
    commentToolTip?: string | JSX.Element;
    likes?: ILike;
    commentCount?: number;
    comments?: IComment[];
    questionText?: string;
    includeLikesAndComments?: boolean;
}

export interface IFeedbackAnswersProps {
    answers: IAnswerProps[];
    className?: string;
    onReact?: (id: string) => void;
    onHasSeenToggle?: (answerId: string, checked: boolean) => void;
    onCommentClick?: (id: string) => void;
    onCommentAdded?: (answerId: string, comment: IComment) => void;
    includeLikesAndComments?: boolean;
}

const FeedbackAnswers = ({
    answers,
    className,
    onCommentAdded,
    includeLikesAndComments,
    onHasSeenToggle,
}: IFeedbackAnswersProps): JSX.Element => {
    const { teamResultsStore: store, userStore } = useContext(StoreContext);
    const employeeId = userStore?.payload?.employeeId;
    const [localAnswers, setLocalAnswers] = useState<IAnswerProps[]>(answers);

    useEffect(() => {
        setLocalAnswers(answers);
    }, [answers, store.translateCommentsEnabled]);

    const handleHasSeenToggle = (id: string, checked: boolean) => {
        setLocalAnswers((prev) =>
            prev.map((ans) =>
                ans.id === id ? { ...ans, hasSeen: checked } : ans,
            ),
        );
        store.managerChecksComment(id, checked);
    };

    const handleLikeToggled = (id: string, newCount: number) => {
        setLocalAnswers((prev) =>
            prev.map((ans) =>
                ans.id === id ? { ...ans, likeCount: newCount } : ans,
            ),
        );
    };

    return (
        <Scroll>
            <ul className={classNames('feedback-answers__results', className)}>
                {localAnswers.map((answer) => (
                    <FeedbackAnswerItem
                        key={answer.id}
                        answer={{
                            ...answer,
                            onHasSeenCheck: (checked) => {
                                handleHasSeenToggle(answer.id!, checked);
                                onHasSeenToggle?.(answer.id!, checked);
                            },
                        }}
                        includeLikesAndComments={includeLikesAndComments}
                        store={store}
                        employeeId={employeeId}
                        onCommentAdded={onCommentAdded}
                        onLikeToggled={handleLikeToggled}
                    />
                ))}
            </ul>
        </Scroll>
    );
};

export default FeedbackAnswers;
